import { useCatalogData } from "../components/CatalogDataContext";
import { MetricBand } from "../components/ui/MetricBand";
import { StatusDot } from "../components/ui/StatusDot";

export function AboutPage() {
  const { models, providers, meta } = useCatalogData();
  const verified = models.filter((m) => m.pricing.confidence === "verified").length;
  const estimated = models.filter((m) => m.pricing.confidence === "estimated").length;
  const unknown = models.filter((m) => m.pricing.confidence === "unknown").length;
  const sota = models.filter((m) => m.qualityRank === "sota").length;
  const good = models.filter((m) => m.qualityRank === "good").length;
  const usable = models.filter((m) => m.qualityRank === "usable").length;
  return (
    <div className="prose">
      <p className="eyebrow">01 / ABOUT</p>
      <div className="page-header-row">
        <h1 className="tl-title" style={{ fontSize: "clamp(1.65rem, 3vw, 2.4rem)" }}>
          Methodology
          <span className="ctx"> / catalog ops</span>
        </h1>
        <StatusDot kind="live" label="PUBLIC CATALOG" />
      </div>
      <MetricBand
        items={[
          { label: "Providers", value: providers.length },
          { label: "Models", value: models.length },
          { label: "Verified", value: verified, tone: "lime" },
          { label: "SOTA", value: sota, tone: "amber" },
        ]}
      />
      <p>
        <strong>ModelMeter</strong> is a public catalog of generative
        image and video model APIs — providers, models, pricing units,
        quality ranks, capability matrices, and documentation links. It is{" "}
        <em>not</em> a Chrome extension or personal spend tracker.
      </p>
      <h2>What we track</h2>
      <ul>
        <li>First-party APIs (OpenAI, Google Gemini, xAI, BFL, Stability, Ideogram, MiniMax, …)</li>
        <li>Cloud hosts (fal.ai, Replicate, Cloudflare Workers AI, Bedrock, Azure, Vertex)</li>
        <li>Creative platforms &amp; labs (Krea aggregator + K2, Pruna performance models)</li>
        <li>Regional / aggregator hosts (Kie, AtlasCloud, BytePlus, Alibaba Wan, Novita, …)</li>
      </ul>
      <h2>Pricing methodology</h2>
      <ul>
        <li><strong>Verified</strong> — from official pricing/docs on <code>lastVerified</code>.</li>
        <li><strong>Estimated</strong> — host listings, credit→USD, or typical configs.</li>
        <li><strong>Unknown</strong> — catalogued without a responsible published number. We never invent verified prices.</li>
      </ul>
      <p>
        <strong>Normalized price</strong> is best-effort for sorting: typically USD/image (~1 MP) or USD/second of video.
      </p>
      <h2>Quality rank</h2>
      <p>
        Each model carries <code>qualityRank</code>: <strong>SOTA</strong>, <strong>Good</strong>, or{" "}
        <strong>Usable</strong> — seeded from public reputation and refinable via Jev admin rescore.
      </p>
      <h2>Capability matrix</h2>
      <p>
        Beyond free-form tags, models expose structured strengths (edit, LoRA, T2I/I2I, T2V/I2V,
        audio-in-video, ControlNet, upscale, inpaint/outpaint, style/moodboard refs, training).
        Catalog chips and detail grids filter on these.
      </p>
      <h2>Intent search — Jev categorization</h2>
      <p>
        Catalog discovery is intent-first: type a natural-language ask into the catalog band
        (example: SOTA image models that are great at edits and cheap).{" "}
        <code>POST /api/intent</code> maps that text with <strong>TypeSafe Jev</strong> only
        (<code>choice</code> / <code>noul</code> / <code>score</code> against saved taxonomies:
        modality, quality rank, strengths, budget, confidence, status) plus light name matching
        for “compare A and B”. Removable chips show what was applied; advanced filters stay behind
        a disclosure. Compare opens in a <strong>modal</strong> with a clean SVG spider (no
        animated orbs). <code>POST /api/nl-search</code> remains 410 Gone;{" "}
        <code>/api/jev-guide</code> is demoted.
      </p>
      <h2>fal catalog sync</h2>
      <p>
        Admin <code>POST /api/admin/sync-fal</code> paginates{" "}
        <code>GET https://api.fal.ai/v1/models?status=active</code>, imports image/video-relevant
        endpoints into KV (deduped against seed), and merges them into <code>GET /api/catalog</code>.
      </p>
      <h2>Daily refresh &amp; confidence</h2>
      <p>
        A Worker cron (<code>0 6 * * *</code> UTC) refreshes pricing stubs, caveats (Reddit + X search),
        and a fal sync batch. Admin PIN unlocks on-demand jobs.
      </p>
      <h2>Community caveats (Reddit + X)</h2>
      <p>
        Caveats sample public Reddit JSON and DuckDuckGo HTML for <code>site:x.com</code> snippets,
        then Jev scores quality and flags issues. Admin can also{" "}
        <code>POST /api/admin/ingest-caveat-texts</code> with pasted tweet texts (box-side browser
        enrichment). Not a scientific benchmark. Cloudflare Browser Rendering is optional if a{" "}
        <code>BROWSER</code> binding is later enabled.
      </p>
      <h2>Catalog snapshot</h2>
      <ul>
        <li>{providers.length} providers · {models.length} models</li>
        <li>
          Confidence mix: {verified} verified · {estimated} estimated · {unknown} unknown
        </li>
        <li>
          Quality mix: {sota} SOTA · {good} Good · {usable} Usable
        </li>
        <li>Seed last updated: {meta.lastUpdated}</li>
        {meta.lastRefreshAt && <li>Last KV refresh: {meta.lastRefreshAt}</li>}
        {meta.lastCaveatsAt && <li>Last caveats pass: {meta.lastCaveatsAt}</li>}
        {meta.lastFalSyncAt && <li>Last fal sync: {meta.lastFalSyncAt} ({meta.falModelCount ?? "?"} imported)</li>}
      </ul>
      <p className="muted">{meta.disclaimer}</p>
    </div>
  );
}
